import * as Sentry from '@sentry/react';

import type { BackendTypes } from '@tf/api';
import { BackendClient } from '@tf/api';

import { getObjectUriByNodeId } from '@/core/api/legacy';
import type { ExtendedReviewContainer } from '@/core/types';
import type { AccountWithReviews } from '@/core/types/account';

const MIN_DELAY_MS = 50;
const MAX_DELAY_MS = 200;
const withRandomDelay = async <T>(fetchFn: () => Promise<T>): Promise<T> => {
	const delay = MIN_DELAY_MS + Math.random() * (MAX_DELAY_MS - MIN_DELAY_MS);
	await new Promise((resolve) => setTimeout(resolve, delay));
	return fetchFn();
};

const BATCH_SIZE = 5;
export async function fetchAllInBatches<InputItem, Result>(
	items: readonly InputItem[],
	worker: (item: InputItem) => Promise<Result>,
): Promise<Result[]> {
	const results: Result[] = [];

	for (let start = 0; start < items.length; start += BATCH_SIZE) {
		const batch = items.slice(start, start + BATCH_SIZE);

		const batchResults = await Promise.all(
			batch.map((item) => {
				return worker(item);
			}),
		);

		batchResults.forEach((result, _index) => {
			results.push(result);
		});
	}

	return results;
}

export const getAccountQuery = async (accountId: number): Promise<AccountWithReviews> => {
	const api = BackendClient.getInstance();

	// * Get account meta
	const [profileId, meta] = await Promise.all([
		getObjectUriByNodeId(accountId),
		api.entityGet({ entityId: accountId }).then((res) => res.json() as Promise<AccountWithReviews['meta']>),
	]);

	// * Get reviews from account
	// TODO: This is a temporary solution, we need to implement batch review fetching
	const connections = meta.listConnections
		.filter((connection) => connection.isReview)
		.flatMap((connection) =>
			connection.listFromEntities.map((entity) => ({
				graphLinkId: entity.graphLinkId,
				graphNodeId: entity.graphNodeId,
			})),
		);

	const reviews: ExtendedReviewContainer[] = await fetchAllInBatches(connections, async (connection) => {
		return withRandomDelay(async () => {
			try {
				const res = await api.reviewGet({ connectionId: connection.graphLinkId });
				const review: BackendTypes.ReviewGetData = await res.json();
				return { ...review, ...connection };
			} catch (err) {
				Sentry.captureMessage('Failed to load review', {
					level: 'error',
					extra: {
						accountId,
						reviewGraphLinkId: connection.graphLinkId,
						httpStatus: err instanceof Response ? err.status : '',
					},
				});
				throw err;
			}
		});
	});

	return { profileId, meta, reviews };
};
